<template>
  <div class="import-tab">

    <!-- ── STARTVY: släpp fil ── -->
    <template v-if="state.phase === 'idle' || state.phase === 'parsing'">
      <div class="card">
        <div class="hero-head">
          <h3>Importera träningsdata</h3>
          <p>Släpp en export från <strong>Daily Strength</strong> eller <strong>Google Takeout</strong>. Du får en översikt över
            vad som hittats, kan mappa övningar och välja bort dubbletter innan något sparas.</p>
        </div>
        <ImportDropZone :busy="state.phase === 'parsing'" :busy-label="state.parseProgress || `Läser ${busyLabel}…`" @files="onFiles" />
        <p v-if="state.error" class="error">{{ state.error }}</p>
        <div class="examples">
          <span>Prova flödet med exempeldata:</span>
          <button class="link-btn" @click="loadMock('daily_strength')">🏋️ Daily Strength</button>
          <button class="link-btn" @click="loadMock('google_takeout')"><span class="g">G</span> Google Takeout</button>
          <button class="link-btn" @click="loadMock('daily_strength_large')">🏋️ 5 år</button>
          <button class="link-btn" @click="loadMock('google_takeout_large')"><span class="g">G</span> 6 år</button>
        </div>
      </div>

      <div class="card">
        <h4>Vad kan importeras?</h4>
        <div class="matrix-wrap">
          <table class="matrix">
            <thead><tr><th></th><th>🏋️ Daily Strength</th><th><span class="g">G</span> Google Takeout</th></tr></thead>
            <tbody>
              <tr v-for="row in matrix" :key="row.key">
                <td class="m-label">{{ row.icon }} {{ row.label }}</td>
                <td :class="row.ds.cls">{{ row.ds.text }}</td>
                <td :class="row.g.cls">{{ row.g.text }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="fine">Tider i CSV-filer tolkas som lokal tid. Om-import av samma fil skapar inga dubbletter – varje pass, aktivitet och mätning får en stabil nyckel.</p>
      </div>

      <div v-if="recent.length" class="card">
        <div class="card-head">
          <h4>Senaste importer</h4>
          <button class="link-btn small" @click="clearRecent">Rensa</button>
        </div>
        <ul class="recent">
          <li v-for="(r, i) in recent" :key="i">
            <span class="r-icon">{{ r.source === 'google_takeout' ? 'G' : '🏋️' }}</span>
            <div class="r-main">
              <span class="r-title">{{ r.sourceLabel }} <em v-if="r.mock">exempeldata</em></span>
              <span class="r-meta">{{ fmtDT(r.at) }} · {{ r.files.join(', ') }}</span>
            </div>
            <span class="r-counts">{{ summarizeCounts(r.counts) }}</span>
          </li>
        </ul>
      </div>
    </template>

    <!-- ── GRANSKA: översiktskort ── -->
    <template v-else-if="state.phase === 'review'">
      <div class="card src-head">
        <span class="src-badge" :class="{ google: bundle.source === 'google_takeout' }">{{ bundle.source === 'google_takeout' ? 'G' : '🏋️' }}</span>
        <div class="src-info">
          <h3>{{ bundle.sourceLabel }} <span v-if="bundle.mock" class="mock-tag">exempeldata</span></h3>
          <p>{{ bundle.files.map(f => f.name).join(', ') }} · {{ fmtSize(bundle.files) }}</p>
          <p v-if="bundle.unknownFiles?.length" class="warn-text">Kände inte igen: {{ bundle.unknownFiles.join(', ') }}</p>
        </div>
        <button class="btn-ghost" @click="reset">Byt fil</button>
      </div>

      <div v-if="state.mode === 'live' && backendReady === false" class="notice">
        <strong>Gym-API:t saknar importstödet.</strong> Uppdatera gymtracker-api-containern till senaste imagen innan du importerar – annars misslyckas batcharna.
      </div>
      <div v-for="(n, i) in bundle.notices || []" :key="i" class="notice soft">{{ n }}</div>

      <div class="cards">
        <ImportTypeCard
          v-for="c in cards" :key="c.def.key"
          :def="c.def" :ds="c.ds" :counts="c.counts" :blocking="c.blocking"
          :cta="ctaFor(c.def.key)" :extra="extraFor(c)" :range="rangeFor(c)"
          @toggle="c.ds.enabled = !c.ds.enabled; touch()"
          @open="openPanel(c.def.key)"
        />
      </div>

      <div class="actionbar">
        <div class="ab-summary">
          <strong>{{ fmtInt(plannedTotal) }} poster</strong> från {{ activeDatasetCount }} datatyp{{ activeDatasetCount === 1 ? '' : 'er' }}
          <span v-if="blockingReasons.length" class="ab-warn">⚠ {{ blockingReasons.join(' · ') }}</span>
        </div>
        <button class="btn-primary" :disabled="!canRun" @click="start">Importera {{ fmtInt(plannedTotal) }} poster</button>
      </div>
    </template>

    <!-- ── KÖR / KLAR ── -->
    <ImportRunView v-else @again="reset" @goto="$emit('goto', $event)" />

    <!-- ── PANEL (bottenark / dialog) ── -->
    <BottomSheet v-if="state.panel" size="wide" :title="panelTitle" @close="closePanel">
      <ExerciseMappingPanel v-if="state.panel === 'sessions'" :exercise-types="exerciseTypes" :muscle-groups="muscleGroups" :muscles="muscles" />
      <ImportReviewPanel v-else :dataset-key="state.panel" :activity-types="state.activityTypes" />
      <div class="panel-foot">
        <span class="pf-info">{{ panelFootInfo }}</span>
        <button class="btn-primary" @click="closePanel">Klar</button>
      </div>
    </BottomSheet>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { gymAuthState, refreshIfNeeded } from '../../../services/gymTrackerAuth.js';
import api from '../../../services/gymTrackerApi.js';
import BottomSheet from '../BottomSheet.vue';
import ImportDropZone from './ImportDropZone.vue';
import ImportTypeCard from './ImportTypeCard.vue';
import ExerciseMappingPanel from './ExerciseMappingPanel.vue';
import ImportReviewPanel from './ImportReviewPanel.vue';
import ImportRunView from './ImportRunView.vue';

import { importState, setBundle, resetImport, openPanel, closePanel, touch, cards, plannedTotal, blockingReasons, canRun } from '../../../services/import/importSession.js';
import { DATASET_DEFS, DATASET_BY_KEY, datasetPlannedCount, datasetDateRange, formatRange, mappingIsReady, fmtInt } from '../../../services/import/importModel.js';
import { parseDailyStrengthFiles } from '../../../services/import/dailyStrength.js';
import { parseTakeoutZip } from '../../../services/import/googleTakeout.js';
import { mockDailyStrengthBundle, mockGoogleBundle, mockDailyStrengthBundleLarge, mockGoogleBundleLarge, mockExisting, FALLBACK_ACTIVITY_TYPES } from '../../../services/import/mockData.js';
import { runImport, buildPlan } from '../../../services/import/importRunner.js';
import { loadAliases, rememberAliases, loadRecentImports, pushRecentImport, clearRecentImports } from '../../../services/import/importStore.js';

// Vad som stöds per källa – för matrisen på startvyn.
const SUPPORT = {
  sessions:     { ds: 'yes',  g: 'no' },
  activities:   { ds: 'no',   g: 'yes' },
  weights:      { ds: 'yes',  g: 'yes' },
  measurements: { ds: 'yes',  g: 'no' },
  height:       { ds: 'yes',  g: 'yes' },
  steps:        { ds: 'no',   g: 'yes' },
  sleep:        { ds: 'no',   g: 'later' },
  heartRate:    { ds: 'no',   g: 'later' },
};
const SUPPORT_CELL = { yes: { text: '✓', cls: 'yes' }, no: { text: '–', cls: 'no' }, later: { text: 'senare', cls: 'later' } };

export default {
  name: 'GymImportTab',
  components: { BottomSheet, ImportDropZone, ImportTypeCard, ExerciseMappingPanel, ImportReviewPanel, ImportRunView },
  props: {
    exerciseTypes: { type: Array, default: () => [] },
    muscleGroups: { type: Array, default: () => [] },
    muscles: { type: Array, default: () => [] },
  },
  emits: ['imported', 'goto'],
  setup(props, { emit }) {
    const state = importState;
    const bundle = computed(() => state.bundle);
    const username = computed(() => gymAuthState.username || '_anon');
    const recent = ref([]);
    const busyLabel = ref('filen');
    // null = okänt, false = servern saknar import-API:t (äldre image), true = ok
    const backendReady = ref(null);
    const serverAliases = ref({});

    const matrix = DATASET_DEFS.map(d => ({
      key: d.key, icon: d.icon, label: d.label,
      ds: SUPPORT_CELL[SUPPORT[d.key].ds], g: SUPPORT_CELL[SUPPORT[d.key].g],
    }));

    async function tokenProvider() { await refreshIfNeeded(); return gymAuthState.token; }

    function ctx() {
      return {
        exerciseTypes: props.exerciseTypes, muscleGroups: props.muscleGroups,
        // Serverns alias vinner över lokala (localStorage är fallback för äldre backend).
        aliases: { ...loadAliases(username.value), ...serverAliases.value },
        activityTypes: state.activityTypes,
      };
    }

    // ── Filer ──
    const readText = file => new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = e => res(e.target.result);
      r.onerror = () => rej(new Error(`Kunde inte läsa ${file.name}`));
      r.readAsText(file, 'UTF-8');
    });

    async function onFiles(files) {
      state.error = '';
      state.phase = 'parsing';
      state.parseProgress = '';
      const progress = msg => { state.parseProgress = msg; };
      try {
        const zips = files.filter(f => /\.zip$/i.test(f.name));
        const csvs = files.filter(f => /\.(csv|txt)$/i.test(f.name));
        let parsed;
        if (zips.length) {
          busyLabel.value = zips[0].name;
          parsed = await parseTakeoutZip(zips[0], { ...ctx(), progress });
        } else if (csvs.length) {
          busyLabel.value = csvs.length === 1 ? csvs[0].name : `${csvs.length} filer`;
          const texts = [];
          for (let i = 0; i < csvs.length; i++) { progress(`Läser ${csvs[i].name} (${i + 1}/${csvs.length})…`); texts.push(await readText(csvs[i])); }
          progress('Tolkar rader…');
          await new Promise(r => setTimeout(r));
          parsed = parseDailyStrengthFiles(csvs.map((f, i) => ({ name: f.name, size: f.size, text: texts[i] })), ctx());
        } else {
          throw new Error('Stöder .csv (Daily Strength) och .zip (Google Takeout).');
        }
        progress('Hämtar befintlig data för dubblettkontroll…');
        const existing = await loadExisting(parsed);
        progress('Jämför med det som redan finns…');
        setBundle(parsed, existing);
      } catch (e) {
        state.error = e.message || String(e);
        state.phase = 'idle';
      } finally {
        state.parseProgress = '';
      }
    }

    async function loadMock(source) {
      state.error = '';
      state.phase = 'parsing';
      state.parseProgress = 'Skapar exempeldata…';
      await new Promise(r => setTimeout(r, 30));
      const c = ctx();
      const b = source === 'google_takeout' ? mockGoogleBundle(c)
        : source === 'google_takeout_large' ? mockGoogleBundleLarge(c)
        : source === 'daily_strength_large' ? mockDailyStrengthBundleLarge(c)
        : mockDailyStrengthBundle(c);
      setBundle(b, mockExisting());
      state.parseProgress = '';
    }

    // Befintlig data på servern – för dubblett-/överlappskontroll. Primärt via
    // importExistingKeys (bara nycklar + datum). Faller tillbaka på paginerade
    // fullobjekt om servern kör en äldre image utan import-API.
    async function loadExisting(parsed) {
      if (!gymAuthState.loggedIn) return {};
      const t = await tokenProvider();
      try {
        const d = await api.importExistingKeys({}, t);
        const k = d.importExistingKeys;
        backendReady.value = true;
        return {
          sessions: k.sessions || [],
          activities: k.activities || [],
          weights: (k.measurementDates || []).map(date => ({ date })),
          measurements: (k.measurementDates || []).map(date => ({ date })),
          steps: k.steps || [],
        };
      } catch (e) {
        backendReady.value = false;
        return loadExistingLegacy(parsed, t);
      }
    }

    async function loadExistingLegacy(parsed, t) {
      const safe = p => p.catch(() => null);

      const sessions = [];
      for (let offset = 0; offset < 20000; offset += 500) {
        const d = await safe(api.getSessions('completed', 500, offset, t));
        const page = d?.gymSessions || [];
        sessions.push(...page);
        if (page.length < 500) break;
      }

      const activities = [];
      const range = parsed ? datasetDateRange(parsed.datasets.activities) : null;
      if (range) {
        for (let y = range.from.getFullYear(); y <= range.to.getFullYear(); y++) {
          const d = await safe(api.getActivities({ from: new Date(y, 0, 1).toISOString(), to: new Date(y + 1, 0, 1).toISOString(), limit: 10000 }, t));
          activities.push(...(d?.activities || []));
        }
      }

      const w = await safe(api.getBodyMeasurements(t));
      const weights = w?.bodyMeasurements || [];
      return { sessions, weights, measurements: weights, activities, steps: [] };
    }

    // ── Kort ──
    const activeDatasetCount = computed(() => { void state.rev; return bundle.value ? Object.values(bundle.value.datasets).filter(ds => datasetPlannedCount(ds) > 0).length : 0; });

    function ctaFor(key) {
      if (key === 'sessions') return 'Mappa övningar';
      if (key === 'activities') return 'Koppla typer & granska';
      return 'Granska';
    }
    function extraFor(c) {
      const ds = c.ds;
      if (c.def.key === 'sessions') {
        const ms = ds.mappings;
        const mapped = ms.filter(m => m.action === 'map' && m.exerciseTypeId).length;
        const created = ms.filter(m => m.action === 'create').length;
        const ignored = ms.filter(m => m.action === 'ignore').length;
        return `${ms.length} övningar: ${mapped} kopplade · ${created} nya · ${ignored} ignoreras`;
      }
      if (c.def.key === 'activities') {
        const tm = ds.typeMappings;
        const ok = tm.filter(t => t.action === 'map' && t.activityTypeId).length;
        return `${tm.length} Google-typer, ${ok} kopplade`;
      }
      if (c.def.key === 'height') {
        const h = ds.items[0];
        return h ? `Senaste: ${h.heightCm} cm (${new Date(h.date).toLocaleDateString('sv-SE')})` : '';
      }
      return '';
    }
    function rangeFor(c) {
      if (c.def.key === 'height' || !c.ds.supported) return '';
      return formatRange(datasetDateRange(c.ds));
    }

    const panelTitle = computed(() => {
      const d = DATASET_BY_KEY[state.panel];
      if (!d) return '';
      return `${d.icon} ${state.panel === 'sessions' ? 'Mappa övningar' : d.label}`;
    });
    const panelFootInfo = computed(() => {
      void state.rev;
      if (!bundle.value || !state.panel) return '';
      const ds = bundle.value.datasets[state.panel];
      if (state.panel === 'sessions') {
        const todo = ds.mappings.filter(m => !mappingIsReady(m)).length;
        return todo ? `${todo} övningar kvar att lösa` : `${fmtInt(datasetPlannedCount(ds))} pass importeras`;
      }
      return `${fmtInt(datasetPlannedCount(ds))} av ${fmtInt(ds.items.length)} valda`;
    });

    // ── Kör ──
    async function start() {
      const b = bundle.value;
      state.plan = buildPlan(b);
      state.progress = {};
      state.result = null;
      state.cancelRequested = false;
      state.phase = 'running';
      const result = await runImport(b, {
        mode: state.mode, api, token: tokenProvider, ctx: ctx(),
        shouldCancel: () => state.cancelRequested,
        onProgress: p => { state.progress[p.datasetKey] = { done: p.done, total: p.total, label: p.label }; },
      });
      state.result = result;
      state.phase = 'done';
      if (!b.mock) rememberAliases(username.value, b.datasets.sessions.mappings);
      pushRecentImport(username.value, {
        at: result.finishedAt, source: b.source, sourceLabel: b.sourceLabel, mock: b.mock,
        files: b.files.map(f => f.name),
        counts: Object.fromEntries(Object.entries(result.datasets).map(([k, v]) => [k, v.done])),
      });
      recent.value = loadRecentImports(username.value);
      if (!b.mock) emit('imported');
    }

    function reset() { resetImport(); }
    function clearRecent() { clearRecentImports(username.value); recent.value = []; }

    // ── Format ──
    const fmtDT = iso => new Date(iso).toLocaleString('sv-SE', { dateStyle: 'medium', timeStyle: 'short' });
    function fmtSize(files) {
      const b = files.reduce((n, f) => n + (f.size || 0), 0);
      return b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} kB`;
    }
    function summarizeCounts(counts) {
      return Object.entries(counts || {}).filter(([, n]) => n > 0)
        .map(([k, n]) => `${n} ${DATASET_BY_KEY[k]?.unit || k}`).join(' · ') || 'inget importerat';
    }

    onMounted(async () => {
      recent.value = loadRecentImports(username.value);
      state.activityTypes = await fetchActivityTypes();
      await fetchServerAliases();
    });

    async function fetchServerAliases() {
      if (!gymAuthState.loggedIn) return;
      try {
        const d = await api.getExerciseAliases(await tokenProvider());
        serverAliases.value = Object.fromEntries((d.exerciseAliases || []).map(a => [a.alias, a.exerciseTypeId]));
        backendReady.value = true;
      } catch { backendReady.value = false; }
    }

    async function fetchActivityTypes() {
      if (!gymAuthState.loggedIn) return FALLBACK_ACTIVITY_TYPES;
      const fields = 'id key nameSv nameEn met category isDistanceBased isCardio iconKey';
      try {
        const t = await tokenProvider();
        try {
          const d = await api.request(`query{activityTypes{${fields} googleFitType}}`, {}, t);
          return d.activityTypes || FALLBACK_ACTIVITY_TYPES;
        } catch {
          const d = await api.request(`query{activityTypes{${fields}}}`, {}, t);
          return d.activityTypes || FALLBACK_ACTIVITY_TYPES;
        }
      } catch { return FALLBACK_ACTIVITY_TYPES; }
    }

    return {
      state, bundle, recent, busyLabel, matrix,
      cards, plannedTotal, blockingReasons, canRun, activeDatasetCount, backendReady,
      onFiles, loadMock, ctaFor, extraFor, rangeFor, openPanel, closePanel, touch, panelTitle, panelFootInfo,
      start, reset, clearRecent, fmtDT, fmtSize, summarizeCounts, fmtInt,
    };
  },
};
</script>

<style scoped>
.import-tab { display: flex; flex-direction: column; gap: 1.1rem; }

.card { background: var(--gym-surface); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-lg); padding: 1.2rem; box-shadow: var(--gym-shadow-sm); display: flex; flex-direction: column; gap: 0.9rem; }
.card h3 { margin: 0; font-size: 1.1rem; color: var(--gym-text); }
.card h4 { margin: 0; font-size: 0.95rem; color: var(--gym-text); }
.card-head { display: flex; justify-content: space-between; align-items: center; }
.hero-head p { margin: 0.35rem 0 0; color: var(--gym-text-muted); font-size: 0.88rem; line-height: 1.55; }
.hero-head strong { color: var(--gym-text); }
.error { margin: 0; color: var(--gym-danger); font-size: 0.88rem; font-weight: 600; }
.fine { margin: 0; font-size: 0.78rem; color: var(--gym-text-faint); line-height: 1.5; }

.examples { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; font-size: 0.84rem; color: var(--gym-text-muted); }
.link-btn { background: none; border: 1px solid var(--gym-border); color: var(--gym-accent); border-radius: var(--gym-radius-pill); padding: 0.3rem 0.8rem; font-weight: 700; font-size: 0.82rem; cursor: pointer; display: inline-flex; gap: 0.35rem; align-items: center; }
.link-btn:hover { background: var(--gym-accent-soft); border-color: var(--gym-accent-border); }
.link-btn.small { padding: 0.2rem 0.6rem; font-size: 0.76rem; border: none; }
.g { display: inline-grid; place-items: center; width: 18px; height: 18px; border-radius: 50%; background: #fff; color: #4285f4; font-weight: 800; font-size: 0.75rem; font-family: Arial, sans-serif; }

.matrix-wrap { overflow-x: auto; }
.matrix { width: 100%; border-collapse: collapse; font-size: 0.86rem; color: var(--gym-text); }
.matrix th, .matrix td { padding: 0.45rem 0.6rem; border-bottom: 1px solid var(--gym-border); text-align: center; white-space: nowrap; }
.matrix th { font-size: 0.78rem; color: var(--gym-text-muted); font-weight: 700; }
.matrix th .g { vertical-align: middle; margin-right: 0.2rem; }
.matrix tr:last-child td { border-bottom: none; }
.m-label { text-align: left !important; font-weight: 600; }
.matrix td.yes { color: var(--gym-success); font-weight: 800; }
.matrix td.no { color: var(--gym-text-faint); }
.matrix td.later { color: var(--gym-warning); font-size: 0.76rem; font-weight: 600; }

.recent { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.45rem; }
.recent li { display: flex; gap: 0.7rem; align-items: center; padding: 0.55rem 0.7rem; background: var(--gym-surface-2); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); font-size: 0.85rem; }
.r-icon { width: 28px; text-align: center; font-weight: 800; color: var(--gym-text); }
.r-main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.r-title { font-weight: 700; color: var(--gym-text); }
.r-title em { font-style: normal; font-size: 0.7rem; color: var(--gym-warning); margin-left: 0.4rem; }
.r-meta { font-size: 0.76rem; color: var(--gym-text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.r-counts { font-size: 0.78rem; color: var(--gym-text-muted); text-align: right; }

/* Källhuvud */
.src-head { flex-direction: row; align-items: center; gap: 0.9rem; }
.src-badge { display: grid; place-items: center; width: 44px; height: 44px; border-radius: 12px; background: var(--gym-surface-2); border: 1px solid var(--gym-border); font-size: 1.4rem; flex-shrink: 0; }
.src-badge.google { background: #fff; color: #4285f4; font-weight: 800; font-family: Arial, sans-serif; }
.src-info { flex: 1; min-width: 0; }
.src-info p { margin: 0.15rem 0 0; font-size: 0.8rem; color: var(--gym-text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.warn-text { color: var(--gym-warning) !important; }
.mock-tag { font-size: 0.7rem; font-weight: 700; padding: 0.15rem 0.5rem; border-radius: var(--gym-radius-pill); background: var(--gym-warning-soft); color: var(--gym-warning); vertical-align: middle; margin-left: 0.3rem; }
.btn-ghost { background: none; border: 1px solid var(--gym-border); color: var(--gym-text-muted); border-radius: var(--gym-radius-sm); padding: 0.45rem 0.8rem; font-size: 0.82rem; font-weight: 600; cursor: pointer; white-space: nowrap; }
.btn-ghost:hover { color: var(--gym-text); border-color: var(--gym-border-strong); }

.notice { background: var(--gym-warning-soft); border: 1px solid var(--gym-warning); color: var(--gym-text); border-radius: var(--gym-radius); padding: 0.7rem 0.9rem; font-size: 0.84rem; line-height: 1.5; }
.notice strong { color: var(--gym-warning); }
.notice.soft { background: var(--gym-accent-softer); border-color: var(--gym-accent-border); }

.cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 0.9rem; }

/* Åtgärdsrad – klistrar sig ovanför bottennaven på mobil */
.actionbar {
  position: sticky; bottom: calc(var(--gym-bottomnav-h) + 0.6rem); z-index: 5;
  display: flex; align-items: center; justify-content: space-between; gap: 0.8rem; flex-wrap: wrap;
  background: var(--gym-surface); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-lg);
  padding: 0.8rem 1rem; box-shadow: var(--gym-shadow);
}
.ab-summary { font-size: 0.88rem; color: var(--gym-text-muted); display: flex; flex-direction: column; gap: 0.15rem; min-width: 0; }
.ab-summary strong { color: var(--gym-text); font-size: 1rem; }
.ab-warn { color: var(--gym-warning); font-weight: 600; font-size: 0.8rem; }

.btn-primary { background: var(--gym-accent); color: var(--gym-on-accent); border: none; padding: 0.7rem 1.4rem; border-radius: var(--gym-radius-sm); font-size: 0.95rem; font-weight: 700; cursor: pointer; transition: background var(--gym-dur) var(--gym-ease), transform var(--gym-dur) var(--gym-ease); white-space: nowrap; }
.btn-primary:hover:not(:disabled) { background: var(--gym-accent-hover); box-shadow: var(--gym-shadow-accent); }
.btn-primary:active:not(:disabled) { transform: translateY(1px) scale(0.99); }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

.panel-foot { display: flex; justify-content: space-between; align-items: center; gap: 0.8rem; padding-top: 0.5rem; border-top: 1px solid var(--gym-border); position: sticky; bottom: 0; background: var(--gym-surface); }
.pf-info { flex: 1; min-width: 0; font-size: 0.82rem; color: var(--gym-text-muted); }
.panel-foot .btn-primary { flex: 0 0 auto; width: auto; }

@media (min-width: 768px) {
  .actionbar { position: static; }
}
@media (max-width: 600px) {
  .cards { grid-template-columns: 1fr; }
  .actionbar { flex-direction: column; align-items: stretch; }
  .actionbar .btn-primary { width: 100%; }
  .src-head { flex-wrap: wrap; }
  .r-counts { display: none; }
  .matrix { font-size: 0.8rem; }
  .matrix th, .matrix td { padding: 0.4rem 0.3rem; white-space: normal; }
  .matrix th { font-size: 0.72rem; }
  .matrix td.later { font-size: 0.7rem; }
}
</style>
